import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  ArrowRight, ShoppingBag, Store, Package, ShieldCheck, Truck, FileText,
  Headphones, Share2, Building2, Sparkles, BadgeCheck,
} from "lucide-react";
import api from "@/lib/api";
import { useStore } from "@/context/StoreContext";
import { ProductCard } from "@/components/marketplace/ProductCard";
import { CategoryCard } from "@/components/marketplace/CategoryCard";
import { VendorCard } from "@/components/marketplace/VendorCard";
import { SectionHeader } from "@/components/marketplace/SectionHeader";
import { LoadingState } from "@/components/common/States";

const Section = ({ className = "", children }) => (
  <section className={`max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 sm:py-16 ${className}`}>{children}</section>
);

function Hero({ banner }) {
  return (
    <section className="relative bg-secondary text-white overflow-hidden">
      <div className="absolute inset-0">
        <img src={banner?.image} alt="" className="w-full h-full object-cover opacity-30" />
        <div className="absolute inset-0 bg-gradient-to-r from-[#5C061C] via-[#5C061C]/85 to-[#7A1C2C]/40" />
      </div>
      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 sm:py-24 lg:py-28">
        <div className="max-w-2xl fade-up">
          <span className="eyebrow text-primary-foreground/90">India's Spiritual Marketplace</span>
          <h1 className="mt-3 text-4xl sm:text-5xl lg:text-6xl font-serif font-bold leading-tight">
            Braj Ki Bhakti,<br />Aapke Ghar Tak
          </h1>
          <p className="mt-4 text-base sm:text-lg text-white/85 max-w-xl">
            Spiritual Products • Trusted Vendors • B2B Wholesale • Pan India Delivery
          </p>
          <div className="mt-8 flex flex-wrap gap-3">
            <Link to="/shop" data-testid="hero-shop-now" className="inline-flex items-center gap-2 rounded-full bg-primary text-primary-foreground font-semibold px-6 py-3 hover:bg-[hsl(28_90%_38%)] transition-colors">
              <ShoppingBag size={18} /> Shop Now
            </Link>
            <Link to="/vendor/register" data-testid="hero-sell" className="inline-flex items-center gap-2 rounded-full bg-white/10 border border-white/25 backdrop-blur font-semibold px-6 py-3 hover:bg-white/20 transition-colors">
              <Store size={18} /> Sell on Shri Vrindavan Shop
            </Link>
            <Link to="/b2b" data-testid="hero-wholesale" className="inline-flex items-center gap-2 rounded-full bg-white/10 border border-white/25 backdrop-blur font-semibold px-6 py-3 hover:bg-white/20 transition-colors">
              <Package size={18} /> Buy Wholesale
            </Link>
          </div>
        </div>
      </div>
    </section>
  );
}

const ProductRow = ({ products }) => (
  <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 sm:gap-6">
    {products.map((p) => (
      <ProductCard key={p.slug} product={p} />
    ))}
  </div>
);

export default function Home() {
  const { b2bMode } = useStore();
  const [data, setData] = useState(null);

  useEffect(() => {
    Promise.all([
      api.get("/banners"),
      api.get("/categories"),
      api.get("/vendors"),
      api.get("/home/sections"),
    ]).then(([banners, cats, vendors, sections]) => {
      const byKey = (k) => sections.data.find((s) => s.key === k);
      const trending = { data: { items: byKey("popular")?.products || [] } };
      const best = { data: { items: byKey("best-sellers")?.products || [] } };
      const fresh = { data: { items: byKey("new-arrivals")?.products || [] } };
      setData({
        banners: banners.data,
        categories: cats.data,
        vendors: vendors.data,
        trending: trending.data.items,
        sections: sections.data,
        best: best.data.items,
        fresh: fresh.data.items,
      });
    });
  }, []);

  if (!data) return <LoadingState label="Loading marketplace..." />;

  const byPlace = (p) => data.banners.find((b) => b.placement === p);
  const braj = byPlace("collection");
  const festival = byPlace("festival");
  const referral = byPlace("referral");

  return (
    <div>
      <Hero banner={byPlace("hero")} />

      {/* Shop by category */}
      <Section>
        <SectionHeader
          eyebrow="Explore"
          title="Shop by Category"
          subtitle="From sacred malas to deity vigrah and complete pooja kits — everything for your devotion."
          action={<Link to="/shop" className="hidden sm:inline-flex items-center gap-1 text-sm font-semibold text-secondary hover:gap-2 transition-all">View all <ArrowRight size={15} /></Link>}
        />
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 sm:gap-6">
          {data.categories.map((c) => (
            <CategoryCard key={c.slug} category={c} />
          ))}
        </div>
      </Section>

      {/* Trending */}
      <div className="braj-pattern">
        <Section>
          <SectionHeader eyebrow="Hot Right Now" title={data.sections.find((x) => x.key === "popular")?.title || "Popular Products"} />
          <ProductRow products={data.trending} />
        </Section>
      </div>

      {/* B2B banner */}
      <Section>
        <div className="rounded-2xl overflow-hidden bg-[#1F1610] text-white grid lg:grid-cols-2">
          <div className="p-8 sm:p-12 flex flex-col justify-center">
            <span className="eyebrow text-primary">Wholesale Marketplace</span>
            <h2 className="mt-2 text-3xl sm:text-4xl font-serif font-bold">Buy Wholesale. Grow Your Business.</h2>
            <p className="mt-3 text-stone-300 max-w-lg">
              Connect with verified vendors, manufacturers and suppliers for bulk spiritual and traditional products, with GST invoicing and MOQ pricing.
            </p>
            <div className="mt-6 flex flex-wrap gap-3">
              <Link to="/b2b" data-testid="home-explore-b2b" className="inline-flex items-center gap-2 rounded-full bg-primary text-primary-foreground font-semibold px-6 py-3">
                <Building2 size={18} /> Explore B2B
              </Link>
              <Link to="/b2b/request-quote" data-testid="home-request-quote" className="inline-flex items-center gap-2 rounded-full border border-white/25 font-semibold px-6 py-3 hover:bg-white/10">
                <FileText size={18} /> Request Bulk Quote
              </Link>
            </div>
          </div>
          <div className="relative min-h-[220px]">
            <img src={byPlace("b2b")?.image} alt="B2B wholesale" className="absolute inset-0 w-full h-full object-cover" />
          </div>
        </div>
      </Section>

      {/* Best sellers */}
      <Section>
        <SectionHeader eyebrow="Loved by Devotees" title={data.sections.find((x) => x.key === "best-sellers")?.title || "Best Sellers"} />
        <ProductRow products={data.best} />
      </Section>

      {/* Featured vendors */}
      <div className="bg-accent/40">
        <Section>
          <SectionHeader
            eyebrow="Trusted Sellers"
            title="Featured Vendors"
            subtitle="Verified artisans and sellers from Vrindavan, Mathura, Jaipur & Varanasi."
            action={<Link to="/vendors" className="hidden sm:inline-flex items-center gap-1 text-sm font-semibold text-secondary">All vendors <ArrowRight size={15} /></Link>}
          />
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {data.vendors.slice(0, 4).map((v) => (
              <VendorCard key={v.slug} vendor={v} />
            ))}
          </div>
        </Section>
      </div>

      {/* Braj + Festival collections */}
      <Section>
        <div className="grid md:grid-cols-2 gap-6">
          {[braj, festival].map((b) => (
            <Link key={b?.id} to={b?.link || "/shop"} className="relative rounded-2xl overflow-hidden group min-h-[240px] flex items-end">
              <img src={b?.image} alt={b?.title} className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
              <div className="absolute inset-0 bg-gradient-to-t from-black/75 to-transparent" />
              <div className="relative p-6 text-white">
                <Sparkles size={18} className="text-primary mb-2" />
                <h3 className="font-serif font-bold text-2xl">{b?.title}</h3>
                <p className="text-sm text-white/80 mt-1 max-w-xs">{b?.subtitle}</p>
                <span className="mt-3 inline-flex items-center gap-1 text-sm font-semibold text-primary-foreground">{b?.cta} <ArrowRight size={15} /></span>
              </div>
            </Link>
          ))}
        </div>
      </Section>

      {/* New arrivals */}
      <Section>
        <SectionHeader eyebrow="Just In" title={data.sections.find((x) => x.key === "new-arrivals")?.title || "New Arrivals"} />
        <ProductRow products={data.fresh} />
      </Section>

      {data.sections.filter((x) => !["popular", "best-sellers", "new-arrivals"].includes(x.key) && x.products.length > 0).map((sec) => (
        <Section key={sec.key}>
          <div data-testid={`home-section-${sec.key}`}>
            <SectionHeader eyebrow={sec.subtitle || "Curated"} title={sec.title} />
            <ProductRow products={sec.products} />
          </div>
        </Section>
      ))}

      {/* Referral */}
      <Section>
        <div className="relative rounded-2xl overflow-hidden bg-secondary text-white p-8 sm:p-14 text-center">
          <img src={referral?.image} alt="" className="absolute inset-0 w-full h-full object-cover opacity-20" />
          <div className="relative max-w-2xl mx-auto">
            <Share2 size={30} className="mx-auto text-primary mb-3" />
            <h2 className="text-3xl sm:text-4xl font-serif font-bold">Share. Shop. Earn.</h2>
            <p className="mt-3 text-white/85">
              Refer customers to eligible Shri Vrindavan Shop products and participate in the platform's referral program.
            </p>
            <Link to="/referral" data-testid="home-referral-btn" className="mt-6 inline-flex items-center gap-2 rounded-full bg-primary text-primary-foreground font-semibold px-6 py-3">
              Join Referral Program <ArrowRight size={17} />
            </Link>
          </div>
        </div>
      </Section>

      {/* Trust */}
      <div className="bg-accent/40">
        <Section className="py-10 sm:py-12">
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-6 text-center">
            {[
              [ShieldCheck, "Verified Vendors"],
              [ShoppingBag, "Secure Shopping"],
              [FileText, "GST Invoice"],
              [Truck, "Pan India Delivery"],
              [Building2, "B2B Wholesale"],
              [Headphones, "Customer Support"],
            ].map(([Icon, label]) => (
              <div key={label} className="flex flex-col items-center gap-2">
                <div className="h-12 w-12 rounded-xl bg-secondary/10 text-secondary grid place-items-center">
                  <Icon size={22} />
                </div>
                <span className="text-xs sm:text-sm font-medium text-stone-700">{label}</span>
              </div>
            ))}
          </div>
        </Section>
      </div>

      {/* Blog */}
      <Section>
        <SectionHeader
          eyebrow="Knowledge"
          title="Spiritual Blog & Guides"
          action={<Link to="/blog" className="hidden sm:inline-flex items-center gap-1 text-sm font-semibold text-secondary">All articles <ArrowRight size={15} /></Link>}
        />
        <BlogPreview />
      </Section>
    </div>
  );
}

function BlogPreview() {
  const [blogs, setBlogs] = useState([]);
  useEffect(() => {
    api.get("/blogs").then((r) => setBlogs(r.data.slice(0, 3)));
  }, []);
  return (
    <div className="grid md:grid-cols-3 gap-6">
      {blogs.map((b) => (
        <Link key={b.slug} to={`/blog`} className="rounded-xl overflow-hidden border border-border bg-card card-hover">
          <div className="aspect-[16/9] overflow-hidden bg-muted">
            <img src={b.image} alt={b.title} loading="lazy" className="w-full h-full object-cover" />
          </div>
          <div className="p-4">
            <span className="eyebrow text-[10px]">{b.category}</span>
            <h3 className="mt-1 font-serif font-semibold text-lg text-stone-900 leading-snug line-clamp-2">{b.title}</h3>
            <p className="mt-1 text-sm text-stone-500 line-clamp-2">{b.excerpt}</p>
          </div>
        </Link>
      ))}
    </div>
  );
}
